import React, { useState } from 'react';
import { Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal, Radio, Row, Select, Space, Table, Tag } from 'antd';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { checkPermission } from '@/utils/permission';
import { paymentMethodOptions, renderMoney } from '@/utils/helpers';
import {
    buildAutomaticDepositAllocations,
    buildDepositAllocationPayload,
    getDepositAllocationError,
} from './customerDepositAllocation';
import { transferCodeText, transferHistoryText } from './customerFundTransferPresentation';
import {
    applyCustomerDeposit,
    receiveCustomerDeposit,
    refundCustomerDeposit,
    updateReceipt,
} from '@/redux/actions/receiptActions';
import { getDepositStatusLabel, getDepositTransactionLabel } from '@/utils/receiptCollectionResult';
const makeKey = prefix => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const text = (value, fallback = '—') => value == null || value === '' ? fallback : String(value);
const moneyInputProps = {
    formatter: value => `${value == null ? '' : value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    parser: value => (value || '').replace(/,/g, ''),
};
const maxPaymentMethods = paymentMethodOptions.length;
const buildFormValues = (nextMode, deposit, customerBranchId) => ({
    effective_at: nextMode === 'update' && deposit?.received_at
        ? moment(deposit.received_at) : moment(),
    payment_method: null,
    branch_id: nextMode === 'update' ? (deposit?.branch_id || customerBranchId || null) : (customerBranchId || null),
    target_customer_service_ids: deposit?.target_customer_service_ids ||
        (deposit?.target_customer_service_id ? [deposit.target_customer_service_id] : []),
    amount: nextMode === 'refund'
        ? Number(deposit?.current_balance || 0)
        : nextMode === 'update' ? Number(deposit?.original_amount || 0) : null,
    payment_breakdowns: nextMode === 'update'
        ? ((deposit?.payment_breakdowns || []).length ? deposit.payment_breakdowns : [{
            payment_method: deposit?.payment_method,
            amount: Number(deposit?.original_amount || 0),
            provider_name: 'VNĐ',
        }]).map(row => ({
            payment_method: row.payment_method,
            amount: Number(row.amount || 0),
            provider_name: row.provider_name || 'VNĐ',
        }))
        : undefined,
    note: nextMode === 'update' ? (deposit?.receipt_note || '') : undefined,
    commission_revision: nextMode === 'update' ? Number(deposit?.commission_revision || 1) : undefined,
    idempotency_key: makeKey(`deposit-${nextMode}`),
});

/** Nhận, cập nhật, sử dụng, trả và tra cứu tiền cọc của khách hàng. */
export default function CustomerDepositActions({
    customerId,
    data,
    reload,
    autoOpenReceive = false,
    autoOpenUpdate = false,
    initialDeposit = null,
    showActions = true,
    onClose,
    renderActions,
}) {
    const dispatch = useDispatch();
    const branches = useSelector(state => state.config.branches || []);
    const [form] = Form.useForm();
    const initialMode = autoOpenUpdate && initialDeposit ? 'update' : (autoOpenReceive ? 'receive' : null);
    const [mode, setMode] = useState(initialMode);
    const [selected, setSelected] = useState(initialMode === 'update' ? initialDeposit : null);
    const [selectedServiceIds, setSelectedServiceIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const canReceive = checkPermission('customer_deposit', 'receive');
    const canApply = checkPermission('customer_deposit', 'apply');
    const canUpdate = checkPermission('receipt', 'update');
    const canRefund = checkPermission('customer_deposit', 'refund');
    const canView = checkPermission('customer_deposit', 'view');
    const deposits = data.active_deposits || [];
    const allServices = data.services || [];
    const services = allServices.filter(item => Number(item.outstanding_amount) > 0);
    const serviceOptions = services.map(item => ({
        value: item.id,
        label: `${text(item.name, `Dịch vụ #${item.id}`)} · còn nợ ${renderMoney(item.outstanding_amount)}`,
    }));
    const targetServiceOptions = allServices.map(item => ({
        value: item.id,
        label: text(item.name, `Dịch vụ #${item.id}`),
    }));
    const financialUpdateLocked = mode === 'update' && !!selected?.financial_update_locked;

    const open = (nextMode, deposit = null) => {
        setSelected(deposit);
        setSelectedServiceIds([]);
        setMode(nextMode);
        form.resetFields();
        form.setFieldsValue(buildFormValues(nextMode, deposit, data.customer_branch_id));
    };

    const close = () => {
        setMode(null);
        setSelected(null);
        setSelectedServiceIds([]);
        form.resetFields();
        if ((autoOpenReceive || autoOpenUpdate) && onClose) onClose();
    };
    const command = values => {
        const common = {
            deposit_id: selected?.id,
            reason: values.reason,
            idempotency_key: values.idempotency_key,
        };
        if (mode === 'receive') return dispatch(receiveCustomerDeposit({
            customer_id: customerId,
            amount: values.amount,
            received_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
            payment_method: values.payment_method,
            branch_id: values.branch_id,
            payment_breakdowns: [{ payment_method: values.payment_method, amount: values.amount }],
            target_customer_service_ids: values.target_customer_service_ids || [],
            reason: values.reason,
            idempotency_key: values.idempotency_key,
        }));
        if (mode === 'apply') return dispatch(applyCustomerDeposit({
            ...common, allocations: buildDepositAllocationPayload(
                values.customer_service_ids, values.allocation_amounts
            ),
            applied_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
        }));
        if (mode === 'update') return dispatch(updateReceipt(selected.source_receipt_id, {
            customer_id: customerId,
            amount_paid: values.amount,
            payment_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
            branch_id: values.branch_id,
            payment_breakdowns: values.payment_breakdowns || [],
            target_customer_service_ids: values.target_customer_service_ids || [],
            note: values.note || '',
            commission_revision: values.commission_revision,
            deposit_update_key: values.idempotency_key,
        }));
        return dispatch(refundCustomerDeposit({
            ...common, amount: values.amount, payment_method: values.payment_method,
            refund_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'),
        }));
    };

    const submit = () => form.validateFields().then(values => {
        if (mode === 'update') {
            const paymentRows = values.payment_breakdowns || [];
            if (paymentRows.length > maxPaymentMethods) {
                form.setFields([{
                    name: 'payment_breakdowns',
                    errors: [`Mỗi phiếu chỉ được tối đa ${maxPaymentMethods} hình thức thu.`],
                }]);
                return null;
            }
            const paymentMethods = paymentRows.map(row => row.payment_method).filter(Boolean);
            if (new Set(paymentMethods).size !== paymentMethods.length) {
                form.setFields([{
                    name: 'payment_breakdowns',
                    errors: ['Mỗi hình thức thu chỉ được chọn một lần.'],
                }]);
                return null;
            }
            const paymentTotal = paymentRows
                .reduce((sum, row) => sum + Number(row.amount || 0), 0);
            if (paymentTotal !== Number(values.amount || 0)) {
                form.setFields([{
                    name: 'amount',
                    errors: ['Tổng tiền theo các hình thức thu phải bằng số tiền thực thu.'],
                }]);
                return null;
            }
        }
        setSubmitting(true);
        return command(values).then(async () => { await reload(); close(); }).finally(() => setSubmitting(false));
    });

    const title = {
        receive: 'Nhận cọc', apply: 'Sử dụng cọc',
        update: 'Cập nhật phiếu cọc', refund: 'Hoàn cọc',
    }[mode];
    const columns = [
        { title: 'Phiếu thu', dataIndex: 'receipt_code', width: 100, render: value => text(value) },
        { title: 'Ngày nhận', dataIndex: 'received_at', render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—' },
        { title: 'Dịch vụ dự kiến', dataIndex: 'target_service_names', width: 300, render: values => values?.length ? values.join(', ') : 'Chưa xác định' },
        { title: 'Tiền thực thu', dataIndex: 'original_amount', align: 'right', render: renderMoney },
        { title: 'Số dư cọc', dataIndex: 'current_balance', align: 'right', render: renderMoney },
        { title: 'Trạng thái', dataIndex: 'status', render: value => <Tag>{getDepositStatusLabel(value)}</Tag> },
        { title: 'Cập nhật cuối', dataIndex: 'updated_at', render: (value, row) => (value || row.received_at) ? moment(value || row.received_at).format('DD/MM/YYYY HH:mm') : '—' },
        {
            title: 'Thao tác', key: 'actions', render: (_, row) => <Space wrap>
                {canApply && <Button size="small" onClick={() => open('apply', row)}>Sử dụng</Button>}
                {canUpdate && <Button size="small" type="primary" onClick={() => open('update', row)}>Cập nhật</Button>}
                {canRefund && <Button size="small" danger onClick={() => open('refund', row)}>Hoàn cọc</Button>}
            </Space>,
        },
    ];

    return <>
        {showActions && (renderActions ? renderActions({
            openReceive: () => open('receive'),
            openDepositList: () => setMode('list'),
            openDepositHistory: () => setMode('history'),
            canReceive,
            canView,
        }) : <Space wrap>
            {canReceive && <Button type="primary" onClick={() => open('receive')}>Nhận cọc</Button>}
            {canView && <Button onClick={() => setMode('list')}>Quản lý tiền cọc</Button>}
            {canView && <Button onClick={() => setMode('history')}>Lịch sử tiền cọc</Button>}
        </Space>)}
        <Modal visible={mode === 'list'} width={isMobile ? '100%' : '90%'} title="Các khoản cọc còn hiệu lực"
            footer={<Button type="primary" onClick={close}>Đóng</Button>} onCancel={close} maskClosable={false}>
            <Alert type="info" message="Số dư cọc là phần tiền chưa sử dụng. Cập nhật phiếu sẽ đồng bộ tiền thu, nội dung, hình thức thu và dịch vụ dự kiến." style={{ marginBottom: 12 }} />
            <Table rowKey="id" size="small" dataSource={deposits} columns={columns} scroll={{ x: 900 }} pagination={{ pageSize: 10 }} />
        </Modal>
        <Modal visible={mode === 'history'} width={isMobile ? '100%' : '90%'} title="Lịch sử tiền cọc"
            footer={<Button type="primary" onClick={close}>Đóng</Button>} onCancel={close} maskClosable={false}>
            <Alert type="info" message="Mỗi dòng cho biết số dư cọc trước và sau giao dịch. Các giao dịch cũ được giữ nguyên để phục vụ đối chiếu." style={{ marginBottom: 12 }} />
            <Table rowKey="id" size="small" dataSource={data.deposit_history || []}
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 10 }}
                columns={[
                    { title: 'Thời gian', dataIndex: 'effective_at', width: 155, render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—' },
                    { title: 'Loại giao dịch', dataIndex: 'transaction_type', width: 210, render: value => <Tag>{getDepositTransactionLabel(value)}</Tag> },
                    { title: 'Giao dịch đối ứng', width: 260, render: (_, row) => transferHistoryText(row) || '—' },
                    { title: 'Loại nguồn', width: 180, render: (_, row) => row.transfer_code ? 'Tiền cọc' : '—' },
                    { title: 'Lần chuyển', width: 145, render: (_, row) => transferCodeText(row) },
                    { title: 'Phiếu thu', dataIndex: 'receipt_code', width: 105, render: (value, row) => row.transfer_code ? '—' : text(value) },
                    { title: 'Dịch vụ', dataIndex: 'service_name', width: 230, render: value => text(value) },
                    { title: 'Số tiền', dataIndex: 'amount', align: 'right', width: 130, render: (value, row) => `${row.direction === 'DEBIT' ? '-' : row.direction === 'CREDIT' ? '+' : ''}${renderMoney(value)}` },
                    { title: 'Trước', dataIndex: 'balance_before', align: 'right', width: 130, render: renderMoney },
                    { title: 'Sau', dataIndex: 'balance_after', align: 'right', width: 130, render: renderMoney },
                    { title: 'Người thao tác', dataIndex: 'actor_name', width: 250, render: value => text(value, 'Hệ thống') },
                    { title: 'Nội dung', dataIndex: 'reason', width: 260, render: value => text(value) },
                ]} />
        </Modal>
        <Modal visible={!!title} destroyOnClose forceRender width={isMobile ? '100%' : '50%'} title={title}
            okText="Xác nhận" cancelText="Hủy" confirmLoading={submitting} onOk={submit} onCancel={close} maskClosable={false}>
            <Form form={form} layout="vertical"
                initialValues={initialMode ? buildFormValues(initialMode, initialDeposit, data.customer_branch_id) : undefined}>
                <Alert type={mode === 'refund' || financialUpdateLocked ? 'warning' : 'info'} message={financialUpdateLocked
                    ? 'Phiếu đã phát sinh hoàn cọc. Chỉ được sửa nội dung và dịch vụ dự kiến; các trường tài chính đã khóa.' : ({
                        receive: 'Ghi nhận tiền khách đặt cọc. Khoản này chưa tự thanh toán công nợ.',
                        apply: 'Số tiền sử dụng sẽ giảm số dư cọc và công nợ dịch vụ.',
                        update: 'Cập nhật phiếu nguồn và ghi lại chênh lệch, không xóa lịch sử.',
                        refund: 'Hoàn cọc cho khách và giảm số dư cọc. Chuyển cọc dùng thao tác riêng.',
                    }[mode])} style={{ marginBottom: 16 }} />
                {mode === 'refund' && <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={6}>Tiền thu gốc<br /><strong>{renderMoney(selected?.original_amount || 0)}</strong></Col>
                    <Col xs={12} md={6}>Đã hoàn<br /><strong>{renderMoney(selected?.refunded_amount || 0)}</strong></Col>
                    <Col xs={12} md={6}>Tổng có thể hoàn<br /><strong>{renderMoney(selected?.refundable_amount ?? selected?.current_balance ?? 0)}</strong></Col>
                    <Col xs={12} md={6}>Số dư cọc<br /><strong>{renderMoney(selected?.current_balance || 0)}</strong></Col>
                </Row>}
                {(mode === 'receive' || mode === 'refund') && <Form.Item name="amount" label="Số tiền" extra={mode === 'refund' ? 'Không được vượt số dư còn lại của khoản cọc.' : 'Đây là số tiền khách thực tế nộp trong lần này.'} rules={[
                    { required: true, message: 'Vui lòng nhập số tiền.' },
                    { validator: (_, value) => Number(value) > 0 && (!selected || Number(value) <= Number(selected.current_balance)) ? Promise.resolve() : Promise.reject(new Error('Số tiền không hợp lệ hoặc vượt số dư cọc.')) },
                ]}><InputNumber {...moneyInputProps} min={1} precision={0} style={{ width: '100%' }} /></Form.Item>}
                {mode === 'update' && <>
                    <Form.Item name="amount" label="Số tiền thực thu" extra={`Có thể tăng hoặc giảm, nhưng không thấp hơn ${renderMoney(Math.max(1, Number(selected?.original_amount || 0) - Number(selected?.current_balance || 0)))} vì phần còn lại đã phát sinh giao dịch.`} rules={[
                        { required: true, message: 'Vui lòng nhập số tiền thực thu.' },
                        {
                            validator: (_, value) => Number(value) >= Math.max(1,
                                Number(selected?.original_amount || 0) - Number(selected?.current_balance || 0))
                                ? Promise.resolve()
                                : Promise.reject(new Error('Số tiền mới không đủ bù phần cọc đã sử dụng, hoàn hoặc chuyển.')),
                        },
                    ]}><InputNumber {...moneyInputProps} min={1} precision={0} disabled={financialUpdateLocked} style={{ width: '100%' }} /></Form.Item>
                    <Form.List name="payment_breakdowns">
                        {(fields, { add, remove }) => <Form.Item label="Hình thức thu" required>
                            {fields.map(field => {
                                const paymentRows = form.getFieldValue('payment_breakdowns') || [];
                                const usedMethods = paymentRows.filter((_, index) => index !== field.name)
                                    .map(row => row?.payment_method).filter(Boolean);
                                return <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                                    <Form.Item {...field} name={[field.name, 'payment_method']} fieldKey={[field.fieldKey, 'payment_method']}
                                        rules={[{ required: true, message: 'Chọn hình thức thu.' }]}>
                                        <Select style={{ width: 180 }} placeholder="Hình thức" disabled={financialUpdateLocked}
                                            options={(paymentMethodOptions || []).filter(item => !usedMethods.includes(item.value))
                                                .map(item => ({ value: item.value, label: item.label }))} />
                                    </Form.Item>
                                    <Form.Item {...field} name={[field.name, 'amount']} fieldKey={[field.fieldKey, 'amount']}
                                        rules={[{ required: true, message: 'Nhập số tiền.' }]}>
                                        <InputNumber {...moneyInputProps} min={1} precision={0} placeholder="Số tiền"
                                            disabled={financialUpdateLocked} style={{ width: 180 }} />
                                    </Form.Item>
                                    <Button danger disabled={financialUpdateLocked || fields.length === 1}
                                        onClick={() => remove(field.name)}>Xóa</Button>
                                    <Form.Item {...field} name={[field.name, 'provider_name']} fieldKey={[field.fieldKey, 'provider_name']} hidden>
                                        <Input />
                                    </Form.Item>
                                </Space>;
                            })}
                            <Button disabled={financialUpdateLocked || fields.length >= maxPaymentMethods}
                                onClick={() => add({ payment_method: null, amount: null, provider_name: 'VNĐ' })}>
                                Thêm hình thức thu ({fields.length}/{maxPaymentMethods})
                            </Button>
                        </Form.Item>}
                    </Form.List>
                </>}
                {mode === 'apply' && <>
                    <Form.Item name="customer_service_ids" label="Dịch vụ dùng cọc" extra="Số tiền nhập sẽ trừ vào công nợ từng dịch vụ."
                        rules={[{ required: true, type: 'array', min: 1, message: 'Vui lòng chọn ít nhất một dịch vụ.' }]}>
                        <Select mode="multiple" showSearch optionFilterProp="label" options={serviceOptions}
                            onChange={values => {
                                const nextServiceIds = values || [];
                                setSelectedServiceIds(nextServiceIds);
                                form.setFieldsValue({
                                    allocation_amounts: buildAutomaticDepositAllocations(
                                        nextServiceIds, services, selected?.current_balance
                                    ),
                                });
                            }} />
                    </Form.Item>
                    {selectedServiceIds.map(customerServiceId => {
                        const option = serviceOptions.find(item => Number(item.value) === Number(customerServiceId));
                        const service = services.find(item => Number(item.id) === Number(customerServiceId));
                        return <Form.Item key={customerServiceId} name={['allocation_amounts', customerServiceId]}
                            label={`Số tiền phân bổ · ${option?.label || `Dịch vụ #${customerServiceId}`}`}
                            rules={[
                                { required: true, message: 'Vui lòng nhập số tiền phân bổ.' },
                                {
                                    validator: (_, value) => {
                                        const amounts = form.getFieldValue('allocation_amounts') || {};
                                        const error = getDepositAllocationError({
                                            value,
                                            outstandingAmount: service?.outstanding_amount,
                                            allocationAmounts: amounts, customerServiceIds: selectedServiceIds,
                                            customerServiceId, availableBalance: selected?.current_balance
                                        });
                                        return error ? Promise.reject(new Error(error)) : Promise.resolve();
                                    }
                                },
                            ]}>
                            <InputNumber {...moneyInputProps} min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>;
                    })}
                    <Form.Item shouldUpdate noStyle>
                        {() => {
                            const amounts = form.getFieldValue('allocation_amounts') || {};
                            const total = selectedServiceIds.reduce((sum, id) => sum + Number(amounts[id] || 0), 0);
                            return <div style={{ marginBottom: 16 }}>
                                Tổng phân bổ: <strong>{renderMoney(total)}</strong> / Số dư cọc: <strong>{renderMoney(selected?.current_balance || 0)}</strong>
                            </div>;
                        }}
                    </Form.Item>
                </>}
                {(mode === 'receive' || mode === 'update') && <Form.Item name="target_customer_service_ids" label="Dịch vụ dự kiến" extra="Có thể chọn nhiều dịch vụ hoặc để trống khi khách chưa chốt.">
                    <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={targetServiceOptions} />
                </Form.Item>}
                {mode === 'receive' && <Form.Item name="payment_method" label="Hình thức thu" rules={[{ required: true, message: 'Vui lòng chọn hình thức.' }]}>
                    <Radio.Group options={(paymentMethodOptions || []).map(item => ({ value: item.value, label: item.label }))} />
                </Form.Item>}
                {mode === 'refund' && <Form.Item name="payment_method" label="Hình thức hoàn" rules={[{ required: true, message: 'Vui lòng chọn hình thức.' }]}>
                    <Radio.Group options={(paymentMethodOptions || []).map(item => ({ value: item.value, label: item.label }))} />
                </Form.Item>}
                {(mode === 'receive' || mode === 'update') && <Form.Item name="branch_id" label="Cơ sở thực tế thu tiền" rules={[{ required: true, message: 'Vui lòng chọn cơ sở thực tế thu tiền.' }]}>
                    <Select showSearch optionFilterProp="label" disabled={financialUpdateLocked}
                        options={branches.map(branch => ({ value: branch.id, label: branch.name }))} />
                </Form.Item>}
                <Form.Item name="effective_at" label="Thời gian giao dịch" extra="Chọn thời điểm nghiệp vụ thực tế để lịch sử và báo cáo ghi nhận đúng kỳ." rules={[{ required: true, message: 'Vui lòng chọn thời gian.' }]}>
                    <DatePicker showTime disabled={financialUpdateLocked} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name={mode === 'update' ? 'note' : 'reason'} label={mode === 'update' ? 'Nội dung phiếu cọc' : 'Ghi chú (không bắt buộc)'}>
                    <Input.TextArea rows={3} maxLength={1000} />
                </Form.Item>
                {mode === 'update' && <Form.Item name="commission_revision" hidden><Input /></Form.Item>}
                <Form.Item name="idempotency_key" hidden><Input /></Form.Item>
            </Form>
        </Modal>
    </>;
}
